import { useState, type FormEvent } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";

import type { AcademicYear } from "../../services/academicSetupService";
import type { BulkClassCreateInput, Class, ClassCopyYearInput, ClassTeacherCandidate } from "../../services/studentService";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { generatedName, nameAfterEdit, streamProblems, teacherOption, type StreamDraft } from "./classSetupLogic";

/**
 * Class setup forms. Save sits in the modal footer; the server's refusal is
 * shown under the fields.
 */

let nextKey = 0;
const newStream = (capacity = "40"): StreamDraft => ({ key: ++nextKey, section: "", name: "", capacity, room: "", classTeacher: "" });

function TeacherSelect({ value, onChange, teachers, classes, except, loading, label }: {
  value: string;
  onChange: (value: string) => void;
  teachers: ClassTeacherCandidate[];
  classes: Class[];
  except?: number;
  loading: boolean;
  label?: string;
}) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} className="input mt-1" aria-label={label}>
      <option value="">Assign later</option>
      {loading && <option disabled>Loading teachers…</option>}
      {teachers.map((teacher) => (
        <option key={teacher.id} value={teacher.id}>{teacherOption(teacher, classes, except)}</option>
      ))}
    </select>
  );
}

/** A level entered once with all its streams, created together or not at all. */
export function StreamsForm({ years, yearId, classes, teachers, teachersLoading, busy, error, onSave, onClose }: {
  years: AcademicYear[];
  yearId?: number;
  /** The chosen year's classes, to warn about a name already taken. */
  classes: Class[];
  teachers: ClassTeacherCandidate[];
  teachersLoading: boolean;
  busy: boolean;
  error?: string;
  onSave: (data: BulkClassCreateInput) => void;
  onClose: () => void;
}) {
  const [year, setYear] = useState(String(yearId ?? years.find((item) => item.is_current)?.id ?? ""));
  const [level, setLevel] = useState("");
  const [rows, setRows] = useState<StreamDraft[]>([newStream()]);
  const yearClasses = classes.filter((row) => String(row.academic_year) === year);
  const problems = level.trim() ? streamProblems(level, rows, yearClasses) : [];
  const change = (key: number, changes: Partial<StreamDraft>) => setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (problems.length) return;
    onSave({
      academic_year: Number(year),
      level: level.trim(),
      streams: rows.map((row) => ({
        section: row.section.trim() || null,
        name: row.name.trim(),
        capacity: Number(row.capacity),
        room: row.room.trim(),
        class_teacher: Number(row.classTeacher) || null,
      })),
    });
  };
  return (
    <FormModal
      id="streams-form"
      size="4xl"
      title="Add a level and its streams"
      busy={busy}
      error={error ?? (problems.length ? problems.join(" ") : undefined)}
      saveLabel={`Create ${rows.length} ${rows.length === 1 ? "class" : "classes"}`}
      note="Every stream is created together, or none is."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Level" description="The level is entered once; each stream below becomes a class of its own." divided={false} columns={2}>
        <Field label="Academic year">
          <select required value={year} onChange={(event) => setYear(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a year</option>
            {years.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
          </select>
        </Field>
        <Field label="Level" hint="As the school writes it: Baby, Middle, Top, P1 … P7.">
          <input required autoFocus maxLength={20} value={level} onChange={(event) => setLevel(event.target.value)} className="input mt-1" placeholder="e.g. P1" />
        </Field>
      </FormSection>

      <FormSection title="Streams" description="Leave the stream blank if the level has only one class. Each stream can have its own places, room and class teacher." columns={1}>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <fieldset key={row.key} className="rounded-lg border border-gray-200 p-3">
              <legend className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Stream {index + 1}
                {level.trim() && <span className="rounded bg-primary-50 px-1.5 py-0.5 font-medium normal-case tracking-normal text-primary-800">{row.name.trim() || generatedName(level, row.section)}</span>}
              </legend>
              <div className="grid items-start gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1.3fr_90px_1.2fr_1.6fr_auto]">
                <Field label="Stream" optional={rows.length === 1}>
                  <input required={rows.length > 1} maxLength={10} value={row.section} onChange={(event) => change(row.key, { section: event.target.value })} className="input mt-1" placeholder="A, Blue…" />
                </Field>
                <Field label="Class name" optional hint="Made from level and stream.">
                  <input maxLength={50} value={row.name} onChange={(event) => change(row.key, { name: event.target.value })} className="input mt-1" placeholder={level.trim() ? generatedName(level, row.section) : "e.g. P1 A"} />
                </Field>
                <Field label="Places">
                  <input required type="number" min={1} max={500} value={row.capacity} onChange={(event) => change(row.key, { capacity: event.target.value })} className="input mt-1 tabular-nums" />
                </Field>
                <Field label="Room" optional>
                  <input maxLength={100} value={row.room} onChange={(event) => change(row.key, { room: event.target.value })} className="input mt-1" placeholder="e.g. Block A, Room 2" />
                </Field>
                <Field label="Class teacher" optional>
                  <TeacherSelect value={row.classTeacher} onChange={(value) => change(row.key, { classTeacher: value })} teachers={teachers} classes={yearClasses} loading={teachersLoading} label={`Class teacher for stream ${index + 1}`} />
                </Field>
                <button
                  type="button"
                  disabled={rows.length === 1}
                  onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                  className="mt-6 self-start rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label={`Remove stream ${index + 1}`}
                  title={rows.length === 1 ? "A level needs at least one class" : "Remove this stream"}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </fieldset>
          ))}
          <button type="button" className="btn btn-outline gap-1.5" onClick={() => setRows((current) => [...current, newStream(current[current.length - 1]?.capacity)])}>
            <Plus className="h-4 w-4" /> Add another stream
          </button>
        </div>
      </FormSection>
    </FormModal>
  );
}

/** One class: its level, stream, name, places, room, class teacher and notes. */
export function ClassForm({ record, years, classes, teachers, teachersLoading, busy, error, onSave, onClose }: {
  record: Class;
  years: AcademicYear[];
  classes: Class[];
  teachers: ClassTeacherCandidate[];
  teachersLoading: boolean;
  busy: boolean;
  error?: string;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [year, setYear] = useState(String(record.academic_year));
  const [level, setLevel] = useState(record.level);
  const [section, setSection] = useState(record.section ?? "");
  const [name, setName] = useState(record.name);
  const [capacity, setCapacity] = useState(String(record.capacity));
  const [room, setRoom] = useState(record.room ?? "");
  const [teacher, setTeacher] = useState(String(record.class_teacher ?? ""));
  const [notes, setNotes] = useState(record.notes ?? "");
  const finalName = nameAfterEdit(record, level, section, name);
  const tooSmall = Number(capacity) < record.current_student_count;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({
      academic_year: Number(year),
      level: level.trim(),
      section: section.trim() || null,
      name: finalName,
      capacity: Number(capacity),
      room: room.trim(),
      class_teacher: Number(teacher) || null,
      notes: notes.trim(),
    });
  };
  return (
    <FormModal
      id="class-form"
      title={`Edit ${record.name}`}
      busy={busy}
      error={error}
      saveLabel="Save changes"
      note="The other streams of the level are not changed."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Class" divided={false} columns={2}>
        <Field label="Academic year">
          <select required value={year} onChange={(event) => setYear(event.target.value)} className="input mt-1">
            {years.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
          </select>
        </Field>
        <Field label="Level">
          <input required maxLength={20} value={level} onChange={(event) => setLevel(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Stream" optional>
          <input maxLength={10} value={section} onChange={(event) => setSection(event.target.value)} className="input mt-1" placeholder="A, Blue…" />
        </Field>
        <Field label="Class name" hint={finalName !== name.trim() ? `Will be saved as “${finalName}”.` : "Follows the level and stream unless you type your own."}>
          <input maxLength={50} value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Places and room" columns={2}>
        <Field label="Places" hint={tooSmall ? `${record.current_student_count} learners are already in it; the class will show as over capacity.` : `${record.current_student_count} learners in it now.`}>
          <input required type="number" min={1} max={500} value={capacity} onChange={(event) => setCapacity(event.target.value)} className={`input mt-1 tabular-nums ${tooSmall ? "border-amber-400" : ""}`} />
        </Field>
        <Field label="Room" optional>
          <input maxLength={100} value={room} onChange={(event) => setRoom(event.target.value)} className="input mt-1" placeholder="e.g. Block A, Room 2" />
        </Field>
      </FormSection>

      <FormSection title="Class teacher" description="The teacher who takes the register and looks after the class." columns={1}>
        <Field label="Class teacher" optional>
          <TeacherSelect value={teacher} onChange={setTeacher} teachers={teachers} classes={classes} except={record.id} loading={teachersLoading} />
        </Field>
      </FormSection>

      <FormSection title="Notes for staff" description="Where the class meets and anything a teacher new to it should know. Seen by staff only." columns={1}>
        <Field as="div" label="Notes" optional>
          <AIAssistField
            name="notes"
            rows={3}
            value={notes}
            onChange={(next) => setNotes(next)}
            placeholder="e.g. Meets in the new block; two learners use the ramp entrance."
            context={{ key: "students.class_note", params: { id: record.id, level, section, name: finalName, capacity: Number(capacity) || null, room } }}
            draftLabel="Draft notes"
            draftHint="Uses the class's level, stream, places and room. It never adds facts you did not give."
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Start a year with an earlier year's classes. */
export function CopyYearForm({ years, toYear, busy, error, onSave, onClose }: {
  years: AcademicYear[];
  toYear?: number;
  busy: boolean;
  error?: string;
  onSave: (data: ClassCopyYearInput) => void;
  onClose: () => void;
}) {
  const ordered = [...years].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const target = String(toYear ?? years.find((item) => item.is_current)?.id ?? "");
  const [to, setTo] = useState(target);
  const before = ordered.filter((item) => String(item.id) !== to);
  const toStart = ordered.find((item) => String(item.id) === to)?.start_date ?? "";
  const previous = [...before].reverse().find((item) => item.start_date < toStart) ?? before[before.length - 1];
  const [from, setFrom] = useState(String(previous?.id ?? ""));
  const [keepTeachers, setKeepTeachers] = useState(false);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({ from_year: Number(from), to_year: Number(to), keep_teachers: keepTeachers });
  };
  return (
    <FormModal
      id="copy-year-form"
      size="xl"
      title="Copy classes from another year"
      busy={busy}
      error={error}
      saveLabel="Copy classes"
      note="Learners are not moved; promote them separately."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Years" description="Every level and stream is copied with its places and room. Classes the year already has are left as they are." divided={false} columns={2}>
        <Field label="Copy from">
          <select required value={from} onChange={(event) => setFrom(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a year</option>
            {ordered.filter((item) => String(item.id) !== to).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </Field>
        <Field label="Into">
          <select required value={to} onChange={(event) => setTo(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a year</option>
            {ordered.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
          </select>
        </Field>
        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 sm:col-span-2">
          <input type="checkbox" checked={keepTeachers} onChange={(event) => setKeepTeachers(event.target.checked)} className="mt-0.5 h-4 w-4 accent-primary-600" />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-gray-900"><Copy className="h-3.5 w-3.5" /> Keep the same class teachers</span>
            <span className="block text-xs text-gray-500">Leave it unticked to assign class teachers afresh. Teachers who have left are never carried over.</span>
          </span>
        </label>
      </FormSection>
    </FormModal>
  );
}
